// src/store/slices/retirosSlice.ts

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Retiro } from '../../types';
import * as api from '../../api/walletApi';

interface RetirosState {
  items: Retiro[];
  totalPages: number;
  totalElements: number;
  currentPage: number;
  filtroEstado: string;
  loading: boolean;
  actionLoading: string | null; // id del retiro en acción
  error: string | null;
  successMsg: string | null;
}

const initialState: RetirosState = {
  items: [],
  totalPages: 0,
  totalElements: 0,
  currentPage: 0,
  filtroEstado: '',
  loading: false,
  actionLoading: null,
  error: null,
  successMsg: null,
};

// ── Thunks ───────────────────────────────────────────────────────────────────

export const fetchRetiros = createAsyncThunk(
  'retiros/fetch',
  async (
    { estado, page }: { estado?: string; page?: number },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.getRetiros(estado, page ?? 0, 20);
      return res.content;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.mensaje || 'Error al cargar retiros');
    }
  },
);

export const aprobarRetiroThunk = createAsyncThunk(
  'retiros/aprobar',
  async ({ id, observacion }: { id: string; observacion?: string }, { rejectWithValue }) => {
    try {
      const res = await api.aprobarRetiro(id, observacion);
      return res.content!;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.mensaje || 'Error al aprobar');
    }
  },
);

export const rechazarRetiroThunk = createAsyncThunk(
  'retiros/rechazar',
  async ({ id, observacion }: { id: string; observacion?: string }, { rejectWithValue }) => {
    try {
      const res = await api.rechazarRetiro(id, observacion);
      return res.content!;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.mensaje || 'Error al rechazar');
    }
  },
);

export const procesarRetiroThunk = createAsyncThunk(
  'retiros/procesar',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await api.procesarRetiro(id);
      return res.content!;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.mensaje || 'Error al procesar');
    }
  },
);

// ── Slice ────────────────────────────────────────────────────────────────────

const retirosSlice = createSlice({
  name: 'retiros',
  initialState,
  reducers: {
    setFiltroEstado(state, action) {
      state.filtroEstado = action.payload;
      state.currentPage = 0;
    },
    setPage(state, action) {
      state.currentPage = action.payload;
    },
    clearMessages(state) {
      state.error = null;
      state.successMsg = null;
    },
  },
  extraReducers: (builder) => {
    // fetch
    builder.addCase(fetchRetiros.pending, (state) => { state.loading = true; state.error = null; });
    builder.addCase(fetchRetiros.fulfilled, (state, action) => {
      state.loading = false;
      const page = action.payload as any;
      if (page) {
        state.items = page.content ?? page.items ?? [];
        state.totalPages = page.totalPages ?? 0;
        state.totalElements = page.totalElements ?? 0;
      }
    });
    builder.addCase(fetchRetiros.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // aprobar
    builder.addCase(aprobarRetiroThunk.pending, (state, action) => {
      state.actionLoading = action.meta.arg.id;
    });
    builder.addCase(aprobarRetiroThunk.fulfilled, (state, action) => {
      state.actionLoading = null;
      state.successMsg = 'Retiro aprobado correctamente';
      const idx = state.items.findIndex((r) => r.id === action.payload.id);
      if (idx !== -1) state.items[idx] = action.payload;
    });
    builder.addCase(aprobarRetiroThunk.rejected, (state, action) => {
      state.actionLoading = null;
      state.error = action.payload as string;
    });

    // rechazar
    builder.addCase(rechazarRetiroThunk.pending, (state, action) => {
      state.actionLoading = action.meta.arg.id;
    });
    builder.addCase(rechazarRetiroThunk.fulfilled, (state, action) => {
      state.actionLoading = null;
      state.successMsg = 'Retiro rechazado';
      const idx = state.items.findIndex((r) => r.id === action.payload.id);
      if (idx !== -1) state.items[idx] = action.payload;
    });
    builder.addCase(rechazarRetiroThunk.rejected, (state, action) => {
      state.actionLoading = null;
      state.error = action.payload as string;
    });

    // procesar
    builder.addCase(procesarRetiroThunk.pending, (state, action) => {
      state.actionLoading = action.meta.arg;
    });
    builder.addCase(procesarRetiroThunk.fulfilled, (state, action) => {
      state.actionLoading = null;
      state.successMsg = 'Retiro marcado como procesado ✓';
      const idx = state.items.findIndex((r) => r.id === action.payload.id);
      if (idx !== -1) state.items[idx] = action.payload;
    });
    builder.addCase(procesarRetiroThunk.rejected, (state, action) => {
      state.actionLoading = null;
      state.error = action.payload as string;
    });
  },
});

export const { setFiltroEstado, setPage, clearMessages } = retirosSlice.actions;
export default retirosSlice.reducer;
